import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import marked from 'marked';
import { lazyload } from 'react-lazyload';
import ReactTooltip from 'react-tooltip';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';

import animations from '../scss/components/animations.scss';
import { StaticContents } from 'components/ServerSideComponents.jsx';
import ShareTools from './share-tools';

import {
	cube,
	marker,
	megaphone,
	share,
	twitterLogo
} from './images';

import styles from '../scss/components/list-item.scss';

/*
 * This is a List Item to be used in conjunction with
 * the EntryList component.
 *
 * @prop element - the data of the list item element
 */
@lazyload({
	height: 800,
	once: true,
	offset: 1000
})
export default class ListItem extends Component {
	componentDidUpdate(prevProps) {
		if (this.props.isVisible && !prevProps.isVisible && this.props.onItemInViewport) {
			this.props.onItemInViewport(this.props.nameHash);
			const node = document.getElementById(`${this.props.nameHash}-ad`);
			if (node) {
				window.BULBS_ELEMENTS_ADS_MANAGER.loadAds(node);
				window.BULBS_ELEMENTS_ADS_MANAGER.asyncRefreshSlot(node);
			}
		}
	}

	renderExternalLinks(facebookUrl, website, instagramUrl) {
		const websiteComponent = website && <a href={website} target='_blank' key='website'>website</a>;
		const facebookComponent = facebookUrl && <a href={facebookUrl} target='_blank' key='facebook'>Facebook page</a>;
		const instagramComponent = instagramUrl && <a href={instagramUrl} target='_blank' key='instagram'>Instagram</a>;
		const links = [websiteComponent, facebookComponent, instagramComponent].filter(Boolean).reduce((acc, currentValue) => {
			return acc.concat([currentValue, ', ']);
		}, []);
		return (
				<div className={styles.externalLinks}>
					<p>Visit: {links.slice(0, links.length - 1)}.</p>
				</div>
		);
	}

	/*
	 * Renders Twitter content if the user has a handle,
	 * otherwise an empty div
	 */
	renderTwitterContent(twitterAccount) {
		return twitterAccount ? (
			<div className={styles.twitterLogo}>
				<a href={`https://twitter.com/${twitterAccount}`} target='_blank'>
					<div className={styles.twitterHandle}>
						@{twitterAccount}
					</div>
				</a>
			</div>
		) : <div className={styles.twitterEmpty}/>;
	}

	render() {
		const { element, isFloated, nameHash, twitterData, index } = this.props;
		const followers = twitterData && twitterData.followers;
		const formattedFollowers = followers && followers.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		const {
			age,
			bio,
			claimToFame,
			facebookUrl,
			imageUrl,
			influence,
			job,
			location,
			name,
			rank,
			reach,
			sector,
			substance,
			twitterAccount,
			website,
			instagramUrl
		} = element.fields;

		const { id } = element.sys;

		const shareProps = {
			ga: this.props.ga,
			name,
			nameHash,
			rank,
			twitterAccount
		};

		const itemClass = classNames(
			{
				[styles.floated]: isFloated
			},
			styles.entry,
			styles[sector]
		);

		const additionalContentClass = classNames(styles.row_2);

		return (
			<ReactCSSTransitionGroup
				id={nameHash}
				transitionName={{ appear: animations.appear, appearActive: animations.appearActive }}
				transitionAppear={true}
				transitionAppearTimeout={1000}
				transitionLeaveTimeout={1000}
				transitionEnterTimeout={1000}>
				<div className={itemClass} id={id}>
					<div className={styles.entryInner}>
						<div className={styles.row_1}>
							<div className={classNames(styles.col_2, styles[sector])}>
								<div className={classNames(styles.image, styles[sector])}>
									<img src={imageUrl} />
									<span className={styles.mobileRank}>{rank}</span>
								</div>
								<ShareTools {...shareProps} />
							</div>
							<div className={classNames(styles.col_1, styles.entryIntro)}>
								<div className={styles.entryIntroWrap}>
									<h3 className={styles.rank}>{rank}</h3>
									<h2 className={styles.name}>{name}</h2>
									<div className={styles.job}>
										<span dangerouslySetInnerHTML={{ __html: marked(job) }} />
										{this.renderTwitterContent(twitterAccount)}
									</div>
									<StaticContents>
										<div className={styles.reason}>
											{marked(claimToFame)}
										</div>
									</StaticContents>
								</div>
							</div>
						</div>
						<div className={additionalContentClass}>
							<div className={classNames(styles.col_2, styles[sector])}>
								<ul className={styles.entryMeta}>
									<li className={styles.sector}>
										<h3>Sector</h3>
										<p>{sector}</p>
									</li>
									<li className={styles.age}>
										<h3>Age</h3>
										<p>{age}</p>
									</li>
									<li className={styles.location}>
										<StaticContents>
											<div>
												{marker}
											</div>
										</StaticContents>
										<p>{location}</p>
									</li>
								</ul>
								<div className={classNames(styles.statsBlock, styles[sector])}>
									<StaticContents>
										<div className={styles.stat} data-tip='Influence' data-effect='solid' data-place='top'>
											{megaphone}
											<p className={styles.statContents}>{influence}</p>
										</div>
									</StaticContents>
									<StaticContents>
										<div className={styles.stat} data-tip='Reach' data-effect='solid' data-place='top'>
											{share}
											<p className={styles.statContents}>{reach}</p>
										</div>
									</StaticContents>
									<StaticContents>
										<div className={styles.stat} data-tip='Substance' data-effect='solid' data-place='top'>
											{cube}
											<p className={styles.statContents}>{substance}</p>
										</div>
									</StaticContents>
									<StaticContents>
										<div className={styles.stat} data-tip='Twitter Followers' data-effect='solid' data-place='top'>
											{twitterLogo}
											<p className={styles.statContents}>{formattedFollowers || 'n/a'}</p>
										</div>
									</StaticContents>
									<ReactTooltip />
								</div>
							</div>
							<div className={styles.col_1}>
								<StaticContents>
									<div className={styles.bio}>
										{marked(bio)}
									</div>
								</StaticContents>
								{(facebookUrl || website || instagramUrl) && (
								<StaticContents>
									<div className={styles.bio}>{this.renderExternalLinks(facebookUrl, website, instagramUrl)}</div>
								</StaticContents>
								)}
							</div>
						</div>
					</div>
					{index % 2 === 0 && (
						<div className={styles.ad}>
							<div
								is='bulbs-dfp'
								viewport-threshold='0.7'
								class='dfp header'
								data-ad-unit={index === 0 ? 'mid_banner_eagerload' : 'mid_banner'}
								id={`${nameHash}-ad`}
								data-targeting='{"pos":"mid_banner"}'
							/>
						</div>
					)}
				</div>
			</ReactCSSTransitionGroup>
		);
	}
}

ListItem.propTypes = {
	element: PropTypes.object.isRequired,
	focusElement: PropTypes.func.isRequired,
	ga: PropTypes.object.isRequired,
	isFocused: PropTypes.bool,
	isPriority: PropTypes.bool,
	twitterData: PropTypes.object
};



// WEBPACK FOOTER //
// ./theroot/root-100-2019/src/components/list-item.js